import { useNavigate, useLocation, Link } from "react-router-dom";
import { api, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Loader2, Mail, Lock, ArrowLeft, ShieldCheck } from "lucide-react";
import AppleSignInButton from "../components/AppleSignInButton";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

function formatApiErrorDetail(detail) {
  if (detail == null) return i18n.t("auth.errNetwork");
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, login } = useAuth();

  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [registerForm, setRegisterForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    phone: "",
    accept_terms: false,
    terms_accepted: false,
    privacy_policy_accepted: false,
    marketing_opt_in: false,
    marketing_consent: false,
    email_marketing_consent: false,
    sms_marketing_consent: false,
  });
  const [submitting, setSubmitting] = useState(false);

  // Post-register phone verification step
  const [phoneVerifyStep, setPhoneVerifyStep] = useState(null); // null | { phone_masked }
  const [phoneCode, setPhoneCode] = useState("");
  const [phoneVerifying, setPhoneVerifying] = useState(false);
  const [phoneResendCooldown, setPhoneResendCooldown] = useState(0);

  // 2FA / OTP state
  const [otpStep, setOtpStep] = useState(null); // null | { otp_token, email_masked, expires_at }
  const [otpCode, setOtpCode] = useState("");
  const [otpSubmitting, setOtpSubmitting] = useState(false);
  const [trustDevice, setTrustDevice] = useState(false);
  const [otpRemaining, setOtpRemaining] = useState(0);
  const [resendCooldown, setResendCooldown] = useState(0);
  const otpInputRef = useRef(null);

  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from || "/";
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  // Countdown for OTP expiry
  useEffect(() => {
    if (!otpStep?.expires_at) return;
    const tick = () => {
      const ms = new Date(otpStep.expires_at).getTime() - Date.now();
      setOtpRemaining(Math.max(0, Math.floor(ms / 1000)));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [otpStep]);

  // Resend cooldown
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setInterval(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [resendCooldown]);

  // Phone resend cooldown
  useEffect(() => {
    if (phoneResendCooldown <= 0) return;
    const id = setInterval(() => setPhoneResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [phoneResendCooldown]);

  // Auto-focus OTP input
  useEffect(() => {
    if (otpStep && otpInputRef.current) {
      setTimeout(() => otpInputRef.current?.focus(), 50);
    }
  }, [otpStep]);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!loginForm.email || !loginForm.password) {
      toast.error("Completa todos los campos");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/login", loginForm);
      if (data?.requires_otp) {
        const expiresAt = new Date(Date.now() + (data.ttl_seconds || 300) * 1000).toISOString();
        setOtpStep({
          otp_token: data.otp_token,
          channel: data.channel || "email",
          target_masked: data.target_masked || data.email_masked,
          email_masked: data.email_masked,
          expires_at: expiresAt,
        });
        setOtpCode("");
        setResendCooldown(30);
        toast.success(t("auth.toastCodeSent", { target: data.target_masked || data.email_masked }));
      } else {
        login(data);
        toast.success(t("auth.toastWelcome", { name: data.name }));
        navigate(location.state?.from || "/", { replace: true });
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const submitOtp = async (codeOverride) => {
    const code = (codeOverride ?? otpCode).trim();
    if (code.length !== 6 || !/^\d{6}$/.test(code)) {
      toast.error(t("auth.toastEnter6"));
      return;
    }
    if (!otpStep?.otp_token) return;
    setOtpSubmitting(true);
    try {
      const { data } = await api.post("/auth/2fa/verify", {
        otp_token: otpStep.otp_token,
        code,
        trust_device: trustDevice,
      });
      login(data);
      toast.success(t("auth.toastWelcome", { name: data.name }));
      navigate(location.state?.from || "/", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setOtpCode("");
      otpInputRef.current?.focus();
    } finally {
      setOtpSubmitting(false);
    }
  };

  const handleOtpSubmit = (e) => {
    e.preventDefault();
    submitOtp();
  };

  const handleOtpChange = (e) => {
    const v = e.target.value.replace(/\D/g, "").slice(0, 6);
    setOtpCode(v);
    if (v.length === 6 && !otpSubmitting) {
      submitOtp(v);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0 || !otpStep?.otp_token) return;
    try {
      const { data } = await api.post("/auth/2fa/resend", { otp_token: otpStep.otp_token });
      const expiresAt = new Date(Date.now() + (data.ttl_seconds || 300) * 1000).toISOString();
      setOtpStep((prev) => ({
        ...prev,
        otp_token: data.otp_token,
        channel: data.channel || prev?.channel,
        target_masked: data.target_masked || data.email_masked || prev?.target_masked,
        email_masked: data.email_masked || prev?.email_masked,
        expires_at: expiresAt,
      }));
      setOtpCode("");
      setResendCooldown(30);
      toast.success(t("auth.toastNewCode"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const handleSwitchChannel = async (newChannel) => {
    if (!otpStep?.otp_token || otpStep.channel === newChannel) return;
    try {
      const { data } = await api.post("/auth/2fa/switch-channel", {
        otp_token: otpStep.otp_token,
        channel: newChannel,
      });
      const expiresAt = new Date(Date.now() + (data.ttl_seconds || 300) * 1000).toISOString();
      setOtpStep((prev) => ({
        ...prev,
        otp_token: data.otp_token,
        channel: data.channel,
        target_masked: data.target_masked,
        email_masked: data.email_masked,
        expires_at: expiresAt,
      }));
      setOtpCode("");
      setResendCooldown(30);
      toast.success(
        newChannel === "sms"
          ? t("auth.toastCodeSms", { target: data.target_masked })
          : t("auth.toastCodeEmail", { target: data.target_masked }),
      );
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const handleOtpCancel = () => {
    setOtpStep(null);
    setOtpCode("");
    setTrustDevice(false);
  };

  const formatRemaining = (s) => {
    const m = Math.floor(s / 60);
    const ss = s % 60;
    return `${m}:${ss.toString().padStart(2, "0")}`;
  };

  const handleEmailRegister = async (e) => {
    e.preventDefault();
    if (
      !registerForm.first_name.trim() ||
      !registerForm.last_name.trim() ||
      !registerForm.email ||
      !registerForm.password
    ) {
      toast.error("Completa todos los campos");
      return;
    }
    if (!registerForm.phone || registerForm.phone.replace(/\D/g, "").length < 10) {
      toast.error(t("auth.toastValidPhone"));
      return;
    }
    if (registerForm.password.length < 6) {
      toast.error(t("auth.toastPwdMin"));
      return;
    }
    if (!registerForm.accept_terms) {
      toast.error(t("auth.toastAcceptTerms"));
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/register", registerForm);
      login(data);
      // Auto-trigger SMS verification using the now-authenticated session
      try {
        const phoneRes = await api.post("/auth/security/phone/start", {
          phone: registerForm.phone,
        });
        if (phoneRes.data?.already_verified) {
          toast.success(t("auth.toastAccountCreated", { name: data.name }));
          navigate(location.state?.from || "/", { replace: true });
        } else {
          setPhoneVerifyStep({ phone_masked: phoneRes.data?.phone_masked });
          setPhoneResendCooldown(30);
          toast.success(t("auth.toastSmsVerify"));
        }
      } catch (phoneErr) {
        // Account was created; phone SMS just failed. User can verify later from /seguridad.
        toast.warning(t("auth.toastSmsFailed"));
        navigate(location.state?.from || "/", { replace: true });
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyPhone = async (e) => {
    e.preventDefault();
    const code = phoneCode.trim();
    if (code.length < 4) {
      toast.error(t("auth.toastEnterFull"));
      return;
    }
    setPhoneVerifying(true);
    try {
      await api.post("/auth/security/phone/verify", { code });
      toast.success(t("auth.toastPhoneVerified"));
      setPhoneVerifyStep(null);
      setPhoneCode("");
      navigate(location.state?.from || "/", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setPhoneVerifying(false);
    }
  };

  const handleResendPhoneCode = async () => {
    if (phoneResendCooldown > 0) return;
    try {
      await api.post("/auth/security/phone/start", { phone: registerForm.phone });
      toast.success(t("auth.toastResent"));
      setPhoneResendCooldown(30);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const handleSkipPhoneVerify = () => {
    setPhoneVerifyStep(null);
    toast.info(t("auth.toastVerifyLater"));
    navigate(location.state?.from || "/", { replace: true });
  };

  const handleSocialLoginSuccess = (data) => {
    login(data);
    toast.success(t("auth.toastWelcome", { name: data.name }));
    navigate(location.state?.from || "/", { replace: true });
  };

  const features = [
    { img: "/assets/icons3d/car.png", text: t("auth.featCars", "Accede a cientos de autos") },
    { img: "/assets/icons3d/shield.png", text: t("auth.featProtection", "Protección en cada viaje") },
    { img: "/assets/icons3d/star.png", text: t("auth.featHosts", "Hosts verificados") },
    { img: "/assets/icons3d/community.png", text: t("auth.featCommunity", "Comunidad confiable") },
  ];

  return (
    <div className="min-h-screen bg-sand flex" data-testid="login-page">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 mb-8">
            <img
              src="/assets/rivones-logo.jpg"
              alt="Rivones"
              className="w-10 h-10 rounded-xl object-cover"
            />
            <span className="font-heading font-bold text-xl text-agave">Rivones</span>
          </Link>

          <h1 className="text-3xl font-heading font-bold text-agave mb-2">{t("auth.welcome", "Bienvenido")}</h1>
          <p className="text-gray-600 mb-6">{t("auth.welcomeSubtitle", "Inicia sesión o regístrate para comenzar tu aventura")}</p>

          {phoneVerifyStep ? (
            <div data-testid="phone-verify-step">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-2xl bg-emerald-500/10 flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-agave">{t("auth.phoneVerifyTitle")}</h2>
                  <p className="text-sm text-neutral-600">
                    {t("auth.phoneVerifyBody")}{" "}
                    <span className="font-medium text-agave" data-testid="phone-verify-masked">
                      {phoneVerifyStep.phone_masked || registerForm.phone}
                    </span>
                  </p>
                </div>
              </div>

              <form onSubmit={handleVerifyPhone} className="space-y-4" data-testid="phone-verify-form">
                <div>
                  <Label htmlFor="phone-code" className="font-semibold">
                    {t("auth.phoneCodeLabel")}
                  </Label>
                  <Input
                    id="phone-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="\d{4,8}"
                    maxLength={8}
                    placeholder="000000"
                    className="mt-1 h-14 text-center text-2xl font-mono tracking-[0.5em]"
                    value={phoneCode}
                    onChange={(e) => setPhoneCode(e.target.value.replace(/\D/g, "").slice(0, 8))}
                    data-testid="phone-code-input"
                    autoFocus
                  />
                </div>
                <Button
                  type="submit"
                  disabled={phoneVerifying || phoneCode.length < 4}
                  className="w-full h-11 bg-[#111111] hover:bg-neutral-800 text-white rounded-xl font-medium"
                  data-testid="phone-verify-submit-btn"
                >
                  {phoneVerifying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {t("auth.phoneVerifyCta")}
                </Button>
                <div className="flex items-center justify-between text-sm">
                  <button
                    type="button"
                    onClick={handleResendPhoneCode}
                    disabled={phoneResendCooldown > 0}
                    className="text-terracotta font-medium hover:underline disabled:text-neutral-400 disabled:no-underline disabled:cursor-not-allowed"
                    data-testid="phone-resend-btn"
                  >
                    {phoneResendCooldown > 0
                      ? t("auth.phoneResendIn", { s: phoneResendCooldown })
                      : t("auth.phoneResend")}
                  </button>
                  <button
                    type="button"
                    onClick={handleSkipPhoneVerify}
                    className="text-neutral-500 hover:text-[#111111]"
                    data-testid="phone-skip-btn"
                  >
                    {t("auth.phoneSkip")}
                  </button>
                </div>
              </form>
            </div>
          ) : otpStep ? (
            <div data-testid="otp-step">
              <button
                type="button"
                onClick={handleOtpCancel}
                className="inline-flex items-center gap-2 text-sm text-neutral-600 hover:text-[#111111] mb-4"
                data-testid="otp-back-btn"
              >
                <ArrowLeft className="w-4 h-4" />
                {t("auth.back")}
              </button>

              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-2xl bg-emerald-500/10 flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-agave">{t("auth.otpTitle")}</h2>
                  <p className="text-sm text-neutral-600">
                    {t("auth.otpBody")}{" "}
                    <span className="font-medium text-agave">
                      {otpStep.channel === "sms" ? "SMS" : "email"}
                    </span>{" "}
                    a{" "}
                    <span className="font-medium text-agave" data-testid="otp-email-masked">
                      {otpStep.target_masked || otpStep.email_masked}
                    </span>
                  </p>
                </div>
              </div>

              <form onSubmit={handleOtpSubmit} className="space-y-4" data-testid="otp-form">
                <div>
                  <Label htmlFor="otp-code" className="font-semibold">
                    {t("auth.otp6digit")}
                  </Label>
                  <Input
                    id="otp-code"
                    ref={otpInputRef}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="\d{6}"
                    maxLength={6}
                    placeholder="000000"
                    className="mt-1 h-14 text-center text-2xl font-mono tracking-[0.5em]"
                    value={otpCode}
                    onChange={handleOtpChange}
                    data-testid="otp-code-input"
                  />
                  <p className="text-xs text-neutral-500 mt-2" data-testid="otp-countdown">
                    {otpRemaining > 0
                      ? t("auth.otpExpiresIn", { time: formatRemaining(otpRemaining) })
                      : t("auth.otpExpired")}
                  </p>
                </div>

                <label className="flex items-start gap-2 cursor-pointer">
                  <Checkbox
                    checked={trustDevice}
                    onCheckedChange={(v) => setTrustDevice(!!v)}
                    data-testid="otp-trust-device-checkbox"
                    className="mt-0.5"
                  />
                  <span className="text-sm text-neutral-700">
                    {t("auth.otpTrustDevice")}
                  </span>
                </label>

                <Button
                  type="submit"
                  disabled={otpSubmitting || otpCode.length !== 6}
                  className="w-full h-11 bg-[#111111] hover:bg-neutral-800 text-white rounded-xl font-medium"
                  data-testid="otp-verify-btn"
                >
                  {otpSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {t("auth.otpVerifyCta")}
                </Button>

                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={resendCooldown > 0}
                  className="w-full text-center text-sm text-neutral-600 hover:text-[#111111] disabled:opacity-50 disabled:hover:text-neutral-600"
                  data-testid="otp-resend-btn"
                >
                  {resendCooldown > 0
                    ? t("auth.otpResendIn", { s: resendCooldown })
                    : t("auth.otpResend")}
                </button>

                <button
                  type="button"
                  onClick={() => handleSwitchChannel(otpStep.channel === "sms" ? "email" : "sms")}
                  className="w-full text-center text-xs text-neutral-500 hover:text-[#111111]"
                  data-testid="otp-switch-channel-btn"
                >
                  {otpStep.channel === "sms"
                    ? t("auth.otpPreferEmail")
                    : t("auth.otpPreferSms")}
                </button>
              </form>
            </div>
          ) : (
          <>
          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login" data-testid="tab-login">
                {t("auth.loginTab")}
              </TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register">
                {t("auth.registerTab")}
              </TabsTrigger>
            </TabsList>

            {/* LOGIN TAB */}
            <TabsContent value="login">
              <form onSubmit={handleEmailLogin} className="space-y-4" data-testid="login-form">
                <div>
                  <Label htmlFor="login-email">{t("auth.email")}</Label>
                  <div className="relative mt-1">
                    <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input
                      id="login-email"
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      autoCapitalize="off"
                      autoCorrect="off"
                      spellCheck={false}
                      enterKeyHint="next"
                      required
                      placeholder={t("auth.emailPlaceholder")}
                      className="pl-10 h-11"
                      value={loginForm.email}
                      onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                      data-testid="login-email-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="login-password">{t("auth.password")}</Label>
                  <div className="relative mt-1">
                    <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input
                      id="login-password"
                      type="password"
                      autoComplete="current-password"
                      enterKeyHint="go"
                      required
                      placeholder="••••••••"
                      className="pl-10 h-11"
                      value={loginForm.password}
                      onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                      data-testid="login-password-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-11 bg-[#111111] hover:bg-neutral-800 text-white rounded-xl font-medium"
                  data-testid="login-submit-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {t("auth.loginCta")}
                </Button>
                <Link
                  to="/forgot-password"
                  className="block text-center text-sm text-terracotta hover:underline font-medium"
                  data-testid="forgot-password-link"
                >
                  {t("auth.forgotPassword")}
                </Link>
                <button
                  type="button"
                  onClick={() => setLoginForm({ ...loginForm, email: "rivonesapp@gmail.com" })}
                  className="w-full text-center text-xs text-neutral-500 hover:text-[#111111] underline-offset-2 hover:underline"
                  data-testid="admin-quick-fill-btn"
                >
                  {t("auth.adminAccess")}
                </button>
              </form>
            </TabsContent>

            {/* REGISTER TAB */}
            <TabsContent value="register">
              <form onSubmit={handleEmailRegister} className="space-y-4" data-testid="register-form">
                <div>
                  <Label htmlFor="reg-first-name" className="font-semibold">
                    {t("auth.firstNameLabel")}
                  </Label>
                  <Input
                    id="reg-first-name"
                    required
                    autoComplete="given-name"
                    autoCapitalize="words"
                    enterKeyHint="next"
                    placeholder={t("auth.firstNamePlaceholder")}
                    className="mt-1 h-11"
                    value={registerForm.first_name}
                    onChange={(e) => setRegisterForm({ ...registerForm, first_name: e.target.value })}
                    data-testid="register-first-name-input"
                  />
                  <p className="text-xs text-neutral-500 mt-1">
                    {t("auth.firstNameHint")}
                  </p>
                </div>

                <div>
                  <Label htmlFor="reg-last-name" className="font-semibold">
                    {t("auth.lastNameLabel")}
                  </Label>
                  <Input
                    id="reg-last-name"
                    required
                    autoComplete="family-name"
                    autoCapitalize="words"
                    enterKeyHint="next"
                    placeholder={t("auth.lastNamePlaceholder")}
                    className="mt-1 h-11"
                    value={registerForm.last_name}
                    onChange={(e) => setRegisterForm({ ...registerForm, last_name: e.target.value })}
                    data-testid="register-last-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="reg-email" className="font-semibold">
                    {t("auth.email")}
                  </Label>
                  <div className="relative mt-1">
                    <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input
                      id="reg-email"
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      autoCapitalize="off"
                      autoCorrect="off"
                      spellCheck={false}
                      enterKeyHint="next"
                      required
                      placeholder={t("auth.emailPlaceholder")}
                      className="pl-10 h-11"
                      value={registerForm.email}
                      onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                      data-testid="register-email-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="reg-phone" className="font-semibold">
                    {t("auth.phoneLabel")}
                  </Label>
                  <div className="relative mt-1 flex items-stretch">
                    <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-neutral-50 text-sm text-neutral-600 font-medium">
                      🇲🇽 +52
                    </span>
                    <Input
                      id="reg-phone"
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel-national"
                      required
                      placeholder={t("auth.phonePlaceholder")}
                      className="rounded-l-none h-11 flex-1"
                      value={registerForm.phone}
                      onChange={(e) => setRegisterForm({ ...registerForm, phone: e.target.value })}
                      data-testid="register-phone-input"
                    />
                  </div>
                  <p className="text-xs text-neutral-500 mt-1">
                    {t("auth.phoneSmsHint")}
                  </p>
                </div>

                <div>
                  <Label htmlFor="reg-password" className="font-semibold">
                    {t("auth.password")}
                  </Label>
                  <div className="relative mt-1">
                    <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input
                      id="reg-password"
                      type="password"
                      autoComplete="new-password"
                      enterKeyHint="done"
                      required
                      minLength={6}
                      placeholder={t("auth.passwordMinHint")}
                      className="pl-10 h-11"
                      value={registerForm.password}
                      onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                      data-testid="register-password-input"
                    />
                  </div>
                </div>

                <div className="space-y-3 pt-2">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <Checkbox
                      checked={registerForm.accept_terms}
                      onCheckedChange={(v) =>
                        setRegisterForm({
                          ...registerForm,
                          accept_terms: !!v,
                          terms_accepted: !!v,
                          privacy_policy_accepted: !!v,
                        })
                      }
                      data-testid="register-terms-checkbox"
                      className="mt-0.5"
                    />
                    <span className="text-sm text-neutral-700">
                      {t("auth.termsRead")}{" "}
                      <Link
                        to="/terminos"
                        target="_blank"
                        rel="noopener"
                        className="text-terracotta hover:underline font-medium"
                        data-testid="register-terms-link"
                      >
                        {t("auth.termsTermsLink")}
                      </Link>
                      {t("auth.termsCommaThe")}{" "}
                      <Link
                        to="/elegibilidad"
                        target="_blank"
                        rel="noopener"
                        className="text-terracotta hover:underline font-medium"
                        data-testid="register-eligibility-link"
                      >
                        {t("auth.termsEligibilityLink")}
                      </Link>{" "}
                      {t("auth.termsAndThe")}{" "}
                      <Link
                        to="/privacidad"
                        target="_blank"
                        rel="noopener"
                        className="text-terracotta hover:underline font-medium"
                        data-testid="register-privacy-link"
                      >
                        {t("auth.termsPrivacyLink")}
                      </Link>{" "}
                      {t("auth.termsOfRivones")}
                    </span>
                  </label>

                  <div className="pt-1 space-y-2">
                    <p className="text-xs text-neutral-500 font-medium">
                      {t("auth.marketingHeading")}
                    </p>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <Checkbox
                        checked={registerForm.email_marketing_consent}
                        onCheckedChange={(v) =>
                          setRegisterForm({
                            ...registerForm,
                            email_marketing_consent: !!v,
                            marketing_consent:
                              !!v || registerForm.sms_marketing_consent,
                          })
                        }
                        data-testid="register-email-marketing-checkbox"
                        className="mt-0.5"
                      />
                      <span className="text-sm text-neutral-700">
                        {t("auth.emailMarketing")}
                      </span>
                    </label>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <Checkbox
                        checked={registerForm.sms_marketing_consent}
                        onCheckedChange={(v) =>
                          setRegisterForm({
                            ...registerForm,
                            sms_marketing_consent: !!v,
                            marketing_consent:
                              !!v || registerForm.email_marketing_consent,
                          })
                        }
                        data-testid="register-sms-marketing-checkbox"
                        className="mt-0.5"
                      />
                      <span className="text-sm text-neutral-700">
                        {t("auth.smsMarketing")}
                      </span>
                    </label>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={submitting || !registerForm.accept_terms}
                  className="w-full h-11 bg-[#111111] hover:bg-neutral-800 text-white rounded-xl font-medium"
                  data-testid="register-submit-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {t("auth.register")}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-neutral-200" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-sand px-3 text-gray-500">{t("auth.continueWithGoogleDivider")}</span>
            </div>
          </div>

          {/* Google Login Button */}
          <Button
            onClick={handleGoogleLogin}
            variant="outline"
            className="w-full h-11 bg-white hover:bg-gray-50 text-gray-800 border border-neutral-200 rounded-xl font-medium flex items-center justify-center gap-3"
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            {t("auth.continueWithGoogle")}
          </Button>

          {/* Apple Login Button */}
          <div className="mt-3">
            <AppleSignInButton onSuccess={handleSocialLoginSuccess} />
          </div>

          {/* Features */}
          <div className="mt-8 space-y-4">
            {features.map((feature, index) => (
              <div
                key={index}
                className="flex items-center gap-4 text-gray-700 text-sm group"
                data-testid={`auth-feature-${index}`}
              >
                <img
                  src={feature.img}
                  alt=""
                  loading="lazy"
                  className="w-11 h-11 object-contain drop-shadow-lg transition-transform duration-200 group-hover:scale-110"
                />
                <span className="font-medium">{feature.text}</span>
              </div>
            ))}
          </div>
          </>
          )}
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:block flex-1 relative">
        <img
          src="https://images.unsplash.com/photo-1601950453066-28920c3fec44?crop=entropy&cs=srgb&fm=jpg&w=1200"
          alt="Car rental"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-agave/80 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <h2 className="text-3xl font-heading font-bold mb-4">
            {t("auth.sloganTitle")}
          </h2>
          <p className="text-white/80 text-lg">
            {t("auth.sloganBody")}
          </p>
        </div>
      </div>
    </div>
  );
}
